import classNames from "classnames";
import React from "react";

import styles from "../FormField.module.scss";

export interface IFormField {
  id?: string;
  error?: string;
  name?: string;
  readOnly?: boolean;
  registerField?: any;
  isFocused?: boolean;
  isRequired?: boolean;
  autoComplete?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onInput?: (e: React.FocusEvent<HTMLInputElement>) => void;
  checksPresenceName?: (
    e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLElement>,
    name: string,
  ) => void;
}

export const MaskTimeFormField: React.FC<IFormField> = ({
  id,
  error,
  name,
  readOnly = false,
  registerField,
  isFocused,
  onFocus,
  onBlur,
  onInput,
  checksPresenceName,
  autoComplete,
}) => {
  const PATTERN = /\D/g;

  const getInputValue = (value: string) => {
    return value.replace(PATTERN, "");
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (checksPresenceName) {
      checksPresenceName(e, name);
    }
    const input = e.target;
    const position = input.selectionStart;
    let [h, hh, m, mm] = getInputValue(input.value)?.split("") || [];

    const getH = () => {
      const result = { h: "", hh: "", m: "", mm: "" };
      if (+h <= 2) {
        result.h = h;
      }
      if (+h === 2 && +hh <= 3) {
        result.hh = hh;
      }
      if (+h < 2) {
        result.hh = hh ?? "";
      }
      if (!hh) {
        m = "";
        mm = "";
      }
      if (+m <= 5) {
        result.m = m;
      }
      result.mm = mm ?? "";
      const start = `${result.h}${result.hh}`;
      const end = `${result.m}${result.mm}`;
      const separator = start || end ? ":" : "";
      return start + separator + end;
    };
    input.value = getInputValue(input.value) ? getH() : "";
    input.selectionEnd = position + 1;
    if (typeof onInput === "function") {
      onInput(e);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    const position = input.selectionStart;
    if (
      event.key === "ArrowRight" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Backspace" ||
      event.code === "Space"
    ) {
      if (event.key === "Backspace" && !input.value) {
        input.value = " ";
      }
      if (
        event.key === "Backspace" &&
        (getInputValue(input.value).length < 3 || position <= 2)
      ) {
        input.value = " ";
      }
      return;
    } else {
      if (typeof +event.key !== "number") {
        event.preventDefault();
      }
    }
    return input;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData ?? window["clipboardData"];
    const input = e.target as HTMLInputElement;
    const inputNumbersValue = getInputValue(input.value);
    if (pasted) {
      const pastedText = pasted.getData("Text");
      if (PATTERN.test(pastedText)) {
        input.value = inputNumbersValue;
      }
    }
  };

  return (
    <input
      {...registerField}
      id={id}
      className={classNames(styles.input, {
        [styles.error]: error,
        [styles.active]: isFocused,
      })}
      name={name}
      type="text"
      readOnly={readOnly}
      maxLength={5}
      minLength={0}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={(e) => {
        onFocus(e);
        handleInput(e);
      }}
      onBlur={(e) => {
        onBlur(e);
      }}
      autoComplete={autoComplete}
    />
  );
};
